// components/group/feed/items/CourseFeedItemCard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, BookOpen, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import CourseViewerDialog from '../course/CourseViewerDialog';
import { countLessons, formatCurrencyFromCents, formatDuration, totalDurationSeconds } from '../utils/format';
import { getCourseIdFromItem } from '../utils/feedItemType';
import { groupFeedApi } from '../feedApi';

const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

export default function CourseFeedItemCard({ groupId, item }) {
  const { t } = useTranslation();

  const courseId = useMemo(() => getCourseIdFromItem(item), [item]);
  const [open, setOpen] = useState(false);

  const [access, setAccess] = useState(null);
  const [builder, setBuilder] = useState(null);

  const [loadingAccess, setLoadingAccess] = useState(false);
  const [loadingBuilder, setLoadingBuilder] = useState(false);

  // Load access (price + has_access) for correct paywall UI
  useEffect(() => {
    if (!groupId || !courseId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoadingAccess(true);
        const a = await groupFeedApi.getCourseAccess(groupId, courseId);
        if (!cancelled) setAccess(a);
      } catch (err) {
        console.error(err);
        // Don’t hard-fail the card; just fallback to “unknown/free”.
      } finally {
        if (!cancelled) setLoadingAccess(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, courseId]);

  // Load builder payload once so the card shows real counts (modules/lessons)
  useEffect(() => {
    if (!groupId || !courseId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoadingBuilder(true);
        const b = await groupFeedApi.getCourseBuilder(groupId, courseId);
        if (!cancelled) setBuilder(b);
      } catch (err) {
        console.error(err);
        // Ignore; card can still render basic info from feed item.
      } finally {
        if (!cancelled) setLoadingBuilder(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, courseId]);

  // ✅ Netlify CI treats lint warnings as errors.
  // Avoid `builder?.course || {}` and `builder?.modules || []` which create new
  // objects/arrays every render and break react-hooks/exhaustive-deps.
  const course = useMemo(() => {
    return builder?.course || item?.course_preview || EMPTY_OBJ;
  }, [builder?.course, item?.course_preview]);

  const modules = useMemo(() => {
    return Array.isArray(builder?.modules) ? builder.modules : EMPTY_ARR;
  }, [builder?.modules]);

  const title =
    course.title ||
    item?.title ||
    t('feed.course.untitled', { defaultValue: 'Untitled course' });

  const description =
    course.description ||
    item?.body ||
    item?.description ||
    '';

  const imageUrl =
    course.cover_image_url ||
    course.image_url ||
    item?.image_url ||
    null;

  const moduleCount = useMemo(() => {
    const fromCourse = Number(course?.module_count ?? NaN);
    if (Number.isFinite(fromCourse)) return fromCourse;
    const fromPreview = Number(item?.course_preview?.module_count ?? NaN);
    if (Number.isFinite(fromPreview)) return fromPreview;
    return modules.length;
  }, [course, item?.course_preview?.module_count, modules]);

  const lessonCount = useMemo(() => {
    const fromCourse = Number(course?.lesson_count ?? NaN);
    if (Number.isFinite(fromCourse)) return fromCourse;
    const fromPreview = Number(item?.course_preview?.lesson_count ?? NaN);
    if (Number.isFinite(fromPreview)) return fromPreview;
    return countLessons(modules);
  }, [course, item?.course_preview?.lesson_count, modules]);

  const durationSeconds = useMemo(() => totalDurationSeconds(modules), [modules]);

  // ✅ "Price wins" (don’t let a stale `is_free: true` override a real price)
  const currency = String(access?.currency || course?.currency || item?.course_preview?.currency || 'usd').toUpperCase();
  const priceCents = Number(access?.price_cents ?? course?.price_cents ?? item?.course_preview?.price_cents ?? 0) || 0;
  const isFree = priceCents <= 0;
  const isUnlocked = isFree || !!access?.has_access;

  const status = String(course?.status || item?.course_preview?.status || 'draft');

  const openCourse = () => setOpen(true);

  const startCheckout = async () => {
    try {
      const url = await groupFeedApi.startCourseCheckout(groupId, courseId);
      if (!url) {
        toast.error(
          t('feed.course.checkoutMissing', {
            defaultValue: 'Checkout is not wired yet. (No checkout URL returned.)',
          })
        );
        return;
      }
      window.location.href = url;
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          t('feed.course.checkoutError', { defaultValue: 'Failed to start checkout' })
      );
    }
  };

  return (
    <>
      <Card className="border border-slate-200 overflow-hidden">
        {imageUrl && (
          <div className="relative">
            <img src={imageUrl} alt={title} className="w-full h-40 object-cover" />
            {!isUnlocked && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="flex items-center gap-2 text-white text-sm font-medium">
                  <Lock size={16} />
                  {t('feed.course.lockedBadge', { defaultValue: 'Locked' })}
                </div>
              </div>
            )}
          </div>
        )}

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen size={16} className="text-slate-500" />
                <span className="truncate">{title}</span>
              </CardTitle>
              <CardDescription className="text-xs mt-1 space-y-0.5">
                <div className="text-slate-600">
                  {moduleCount} {t('feed.course.modules', { defaultValue: 'modules' })} ·{' '}
                  {loadingBuilder ? '…' : lessonCount} {t('feed.course.lessons', { defaultValue: 'lessons' })} ·{' '}
                  {status}
                </div>
                <div className="text-slate-500">
                  {item?.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                </div>
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-end">
              {loadingAccess ? (
                <Badge variant="outline">
                  {t('feed.course.loadingPrice', { defaultValue: '…' })}
                </Badge>
              ) : isFree ? (
                <Badge variant="outline">{t('feed.course.free', { defaultValue: 'Free' })}</Badge>
              ) : (
                <Badge variant="outline">{formatCurrencyFromCents(priceCents, currency)}</Badge>
              )}

              <Badge variant="outline" className="flex items-center gap-1">
                <PlayCircle size={12} />
                {loadingBuilder ? '…' : lessonCount}{' '}
                {t('feed.course.lessons', { defaultValue: 'lessons' })}
              </Badge>

              {durationSeconds > 0 && (
                <Badge variant="outline">{formatDuration(durationSeconds)}</Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {description && (
            <p className="text-sm text-slate-700 whitespace-pre-line line-clamp-3">
              {description}
            </p>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={openCourse}>
              {t('feed.course.open', { defaultValue: 'Open course' })}
            </Button>

            {!isUnlocked && (
              <Button onClick={startCheckout}>
                <Lock size={16} className="mr-2" />
                {isFree
                  ? t('feed.course.unlock', { defaultValue: 'Unlock' })
                  : t('feed.course.buy', { defaultValue: 'Buy' })}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <CourseViewerDialog
        open={open}
        onOpenChange={setOpen}
        groupId={groupId}
        courseId={courseId}
        item={item}
        initialAccess={access}
        initialBuilder={builder}
        onCheckout={startCheckout}
      />
    </>
  );
}
